import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch,
  TextField, Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  buildAllNoReplyPreferences,
  buildCustomNoReplyPreferences,
  getInclusiveNoReplyEndDay,
  normalizeNoReplyPreferences
} from "../../utils/noReplyFilter";

const NoReplyFilterModal = ({ open, initialValue, onClose, onApply, isAdmin }) => {
  const [value, setValue] = useState(initialValue);
  const [tags, setTags] = useState([]);
  const [kanban, setKanban] = useState([]);
  const [saving, setSaving] = useState(false);
  const [periodMode, setPeriodMode] = useState("all");
  const [startDay, setStartDay] = useState(1);
  const [endDay, setEndDay] = useState(3);

  useEffect(() => {
    const normalized = normalizeNoReplyPreferences(initialValue);
    const inclusiveEnd = getInclusiveNoReplyEndDay(normalized);
    setValue(normalized);
    setPeriodMode(inclusiveEnd === null ? "all" : "custom");
    setStartDay(Number(normalized.deadlineValue) || 1);
    setEndDay(inclusiveEnd === null ? 3 : inclusiveEnd);
  }, [initialValue, open]);
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/tags/list"), api.get("/tag/kanban/")])
      .then(([tagResult, kanbanResult]) => {
        setTags(tagResult.data || []);
        setKanban(kanbanResult.data?.lista || kanbanResult.data?.list || []);
      })
      .catch(toastError);
  }, [open]);

  const includedTags = useMemo(
    () => tags.filter(tag => (value?.includedTagIds || []).includes(tag.id)),
    [tags, value]
  );
  const includedKanban = useMemo(
    () => kanban.filter(tag => (value?.includedKanbanTagIds || []).includes(tag.id)),
    [kanban, value]
  );
  const excludedTags = useMemo(
    () => tags.filter(tag => (value?.excludedTagIds || []).includes(tag.id)),
    [tags, value]
  );
  const excludedKanban = useMemo(
    () => kanban.filter(tag => (value?.excludedKanbanTagIds || []).includes(tag.id)),
    [kanban, value]
  );

  if (!value) return null;
  const change = patch => setValue(current => ({ ...current, ...patch }));
  const startIsValid = Number.isInteger(Number(startDay)) && Number(startDay) >= 1 && Number(startDay) <= 364;
  const endIsValid = Number.isInteger(Number(endDay)) && Number(endDay) >= 1 && Number(endDay) <= 364;
  const rangeIsValid = periodMode === "all" || (startIsValid && endIsValid && Number(endDay) >= Number(startDay));
  const includedIds = new Set([...(value.includedTagIds || []), ...(value.includedKanbanTagIds || [])]);
  const conflictingIds = [...(value.excludedTagIds || []), ...(value.excludedKanbanTagIds || [])]
    .filter(id => includedIds.has(id));

  const selectPeriod = key => {
    setPeriodMode(key);
    if (key === "all") setValue(current => buildAllNoReplyPreferences(current));
  };

  const clearFilters = () => {
    setPeriodMode("all");
    setStartDay(1);
    setEndDay(3);
    setValue(current => buildAllNoReplyPreferences({
      ...current,
      skipWeekends: false,
      includedTagIds: [],
      includedKanbanTagIds: [],
      excludedTagIds: [],
      excludedKanbanTagIds: []
    }));
  };

  const save = async () => {
    if (!rangeIsValid || conflictingIds.length > 0) return;
    const base = {
      ...value,
      includedTagIds: value.includedTagIds || [],
      includedKanbanTagIds: value.includedKanbanTagIds || [],
      excludedTagIds: value.excludedTagIds || [],
      excludedKanbanTagIds: value.excludedKanbanTagIds || []
    };
    const payload = periodMode === "all"
      ? buildAllNoReplyPreferences(base)
      : buildCustomNoReplyPreferences(base, Number(startDay), Number(endDay));
    setSaving(true);
    try {
      const { data } = await api.put("/no-reply/preferences", payload);
      onApply(data.preferences || data);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Atendimentos sem retorno</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          A contagem comeca na sua ultima mensagem manual enviada pelo Lyon. Se voce enviar outra, ela reinicia; quando o cliente responder, o ticket sai da lista.
        </Typography>

        <FormControl variant="outlined" size="small" fullWidth margin="normal">
          <InputLabel>Periodo</InputLabel>
          <Select label="Periodo" value={periodMode}
            onChange={event => selectPeriod(event.target.value)}>
            <MenuItem value="all">Todos em atraso - 1 dia ou mais</MenuItem>
            <MenuItem value="custom">Filtrar periodo personalizado</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 10 }}>
          {periodMode === "all"
            ? "Mostra 100% dos tickets elegiveis sem resposta ha 1 dia ou mais."
            : "Informe o primeiro e o ultimo dia do periodo. Os dois limites sao incluidos."}
        </Typography>

        {periodMode === "custom" && (
          <div style={{ display: "flex", gap: 8, margin: "6px 0 6px", alignItems: "center" }}>
            <TextField label="Inicio (dias)" type="number" variant="outlined" size="small" fullWidth
              inputProps={{ min: 1, max: 364 }} value={startDay}
              onChange={event => setStartDay(event.target.value)} />
            <TextField label="Fim (dias, inclusive)" type="number" variant="outlined" size="small" fullWidth
              inputProps={{ min: 1, max: 364 }} value={endDay}
              onChange={event => setEndDay(event.target.value)} />
          </div>
        )}
        {!rangeIsValid && (
          <Typography variant="caption" color="error" display="block" style={{ marginBottom: 8 }}>
            Informe dias inteiros entre 1 e 364; o fim precisa ser igual ou maior que o inicio.
          </Typography>
        )}
        {periodMode === "custom" && rangeIsValid && (
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
            Exibicao: de {Number(startDay)} dia(s) completo(s) ate {Number(endDay)} dia(s), 23h59.
          </Typography>
        )}

        <FormControlLabel control={<Switch color="primary" checked={value.skipWeekends}
          onChange={event => change({ skipWeekends: event.target.checked })} />}
          label="Nao contar sabado e domingo" />
        {value.skipWeekends && (
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: -6, marginBottom: 8 }}>
            O limite ignora sabados e domingos; o tempo curto mostrado no card continua sendo o tempo corrido.
          </Typography>
        )}

        <Typography variant="subtitle2" style={{ marginTop: 12 }}>Incluir somente</Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          Com Tag e Kanban selecionados, o ticket precisa atender aos dois filtros.
        </Typography>
        <Autocomplete multiple size="small" options={tags} value={includedTags}
          getOptionLabel={option => option.name || ""}
          onChange={(_, selected) => change({ includedTagIds: selected.map(item => item.id) })}
          renderInput={params => <TextField {...params} variant="outlined" label="Incluir somente Tags" margin="normal" />} />
        <Autocomplete multiple size="small" options={kanban} value={includedKanban}
          getOptionLabel={option => option.name || ""}
          onChange={(_, selected) => change({ includedKanbanTagIds: selected.map(item => item.id) })}
          renderInput={params => <TextField {...params} variant="outlined" label="Incluir somente Kanban" margin="normal" />} />

        <Typography variant="subtitle2" style={{ marginTop: 12 }}>Excluir</Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          Se o ticket possuir qualquer item excluido, ele nao aparecera no resultado.
        </Typography>
        <Autocomplete multiple size="small" options={tags} value={excludedTags}
          getOptionLabel={option => option.name || ""}
          onChange={(_, selected) => change({ excludedTagIds: selected.map(item => item.id) })}
          renderInput={params => <TextField {...params} variant="outlined" label="Excluir Tags" margin="normal" />} />
        <Autocomplete multiple size="small" options={kanban} value={excludedKanban}
          getOptionLabel={option => option.name || ""}
          onChange={(_, selected) => change({ excludedKanbanTagIds: selected.map(item => item.id) })}
          renderInput={params => <TextField {...params} variant="outlined" label="Excluir Kanban" margin="normal" />} />
        {conflictingIds.length > 0 && (
          <Typography variant="caption" color="error" display="block">
            A mesma Tag ou coluna nao pode estar em Incluir e Excluir ao mesmo tempo.
          </Typography>
        )}

        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>
          A ordem pode ser invertida com um clique na barra do Sem Retorno depois de aplicar.
        </Typography>
        {isAdmin && (
          <FormControlLabel control={<Switch color="primary" checked={value.onlyMine}
            onChange={event => change({ onlyMine: event.target.checked })} />}
            label="Somente meus atendimentos" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={clearFilters}>Limpar filtros</Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained"
          disabled={saving || !rangeIsValid || conflictingIds.length > 0} onClick={save}>
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoReplyFilterModal;
